import { cellAtWorld, cellCenter, heroWalkable, touchGrid } from '../grid.ts';
import { idiv, imax, isqrt, mulBp } from '../math/fixed.ts';
import type { BattleState, Enemy, Hero } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import { damageEnemy, damageHero, ev } from './combat.ts';

/**
 * What the three acts of the Long Hunt's chapter 4 share (docs/sendas/barbaro.md, «La Isla Blanca»): the ice that opens
 * under whatever stands on it (the grid's plugs: nobody walks open water), the fall into the sea (the hero up again a
 * little hurt; a nightmare, gone), the blows on what is kept (the camp, the fire: the acts' prop npcs).
 *
 * Cost: the plugs set only when something opens or shuts; sorted lists searched by halves.
 */

/** Whether a sorted list of cells has this one. */
export const hasCell = (cells: readonly number[], c: number): boolean => {
  let lo = 0;
  let hi = cells.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = cells[mid] as number;
    if (v === c) return true;
    if (v < c) lo = mid + 1;
    else hi = mid - 1;
  }
  return false;
};

/** The open water on the board now (the grid's plugs), from every list given. */
export const setOpen = (s: BattleState, lists: readonly (readonly number[])[]): void => {
  const all = new Set<number>();
  for (const l of lists) for (const c of l) all.add(c);
  const next = [...all].sort((a, b) => a - b);
  const was = s.plugs ?? [];
  if (was.length === next.length && was.every((c, i) => c === next[i])) return;
  s.plugs = next;
  touchGrid(s);
};

/** The hero into the water and up onto the nearest ice beside (`bp` of its life less). */
export const fallHero = (s: BattleState, h: Hero, bp: number, kind: 'iFall', n = 0): void => {
  damageHero(s, h, mulBp(h.stats.maxHp, bp), null, 'physical', true);
  ev(s, { t: 'senda', kind, npc: '', x: h.x, y: h.y, n });
  let best = -1;
  let bd = 0x7fffffff;
  for (let dq = -4; dq <= 4; dq++)
    for (let dr = -4; dr <= 4; dr++) {
      const c = cellAtWorld(s, h.x + dq * 1000 + dr * 500, h.y + dr * 866);
      if (c < 0 || !heroWalkable(s, c)) continue;
      const p = cellCenter(s.map.radius, c);
      const d = (p.x - h.x) * (p.x - h.x) + (p.y - h.y) * (p.y - h.y);
      if (d < bd) {
        bd = d;
        best = c;
      }
    }
  if (best >= 0) {
    const p = cellCenter(s.map.radius, best);
    h.x = h.destX = h.ax = p.x;
    h.y = h.destY = h.ay = p.y;
  }
  h.path = [];
  h.moving = false;
  h.stun = imax(h.stun, 12);
};

/** A nightmare into the water: gone (the hero's kill, for the reckoning). */
export const drown = (s: BattleState, e: Enemy, h: Hero | undefined): void => {
  damageEnemy(s, e, e.hp + e.maxHp, { pid: h?.pid ?? '', ignoresArmor: true });
};

/** Blows on a thing kept (the camp, the fire: a prop npc by its id). Returns whether it is gone. */
export const hitKept = (s: BattleState, id: string, n: number, x: number, y: number, kind: 'iCampHit' | 'iFireHit'): boolean => {
  const b = s.senda?.npcs.find((p) => p.id === id);
  if (!b || b.hp <= 0) return !b || b.hp <= 0;
  if (n <= 0) return false;
  b.hp = imax(0, b.hp - n);
  ev(s, { t: 'senda', kind: 'hit', npc: id, x, y });
  ev(s, { t: 'senda', kind, npc: id, x, y, n });
  if (b.hp <= 0) ev(s, { t: 'senda', kind: 'down', npc: id, x: b.x, y: b.y });
  return b.hp <= 0;
};

/** The cells a straight line crosses (sorted), sampled every half hex. */
export const lineCells = (s: BattleState, a: Pt, b: Pt): number[] => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  const n = imax(1, idiv(d, 450));
  const out = new Set<number>();
  for (let i = 0; i <= n; i++) {
    const c = cellAtWorld(s, a.x + idiv(dx * i, n), a.y + idiv(dy * i, n));
    if (c >= 0) out.add(c);
  }
  return [...out].sort((x, y) => x - y);
};
